import {
  ActualVsPredictedRow,
  TwoStageDecompEntry,
  TwoStageDecompMap,
  TwoStageDecompRow
} from '../dtc-commerce-mmm-types';

// Each week's actual ÷ predicted KPI. The page reports contribution "normalized to observed": every
// feature's weekly contribution is scaled by this factor so a week's credit adds up to what actually
// sold. The two-stage file's reallocated effect comes unscaled, so it gets the same factor below.
export const buildObservedFactorByDate = (rows: ActualVsPredictedRow[]): Map<string, number> => {
  const factors = new Map<string, number>();
  rows.forEach(r => {
    if (r.expectedKpi > 0) {
      factors.set(r.date, r.actualKpi / r.expectedKpi);
    }
  });
  return factors;
};

// Sums each primary/secondary pair's reallocations over the window. Effect is scaled week by week
// onto the normalized basis of the direct and actualized totals, so a feature's direct total minus
// (or plus) its reallocations lands exactly on its actualized total. Spend is never normalized, so
// it is summed as-is.
export const aggregateTwoStageDecomp = (
  rows: TwoStageDecompRow[],
  actualVsPredicted: ActualVsPredictedRow[],
  startDate: string,
  endDate: string
): TwoStageDecompMap => {
  const observedFactorByDate = buildObservedFactorByDate(actualVsPredicted);
  const startTime = new Date(`${startDate}T00:00:00`).getTime();
  const endTime = new Date(`${endDate}T00:00:00`).getTime();
  const windowRows = rows.filter(r => {
    const t = new Date(`${r.date}T00:00:00`).getTime();
    return t >= startTime && t <= endTime;
  });

  const aggKey = (pName: string, sName: string): string => `${pName}|||${sName}`;
  const agg = new Map<string, TwoStageDecompEntry>();

  windowRows.forEach(r => {
    const key = aggKey(r.primaryName, r.secondaryName);
    const entry = agg.get(key) ?? {
      primaryName: r.primaryName,
      primaryMediaType: r.primaryMediaType,
      secondaryName: r.secondaryName,
      secondaryMediaType: r.secondaryMediaType,
      categoryType: r.categoryType,
      effect: 0,
      spend: 0
    };
    if (r.spendOrEffect === 'effect') {
      entry.effect += r.value * (observedFactorByDate.get(r.date) ?? 1);
    } else if (r.spendOrEffect === 'spend') {
      entry.spend += r.value;
    }
    agg.set(key, entry);
  });

  const result: TwoStageDecompMap = new Map();
  agg.forEach(entry => {
    const existing = result.get(entry.primaryName) ?? [];
    existing.push(entry);
    result.set(entry.primaryName, existing);
  });

  return result;
};
